import { stepToExperienceDocument, type AuthoringDoc } from '@personalizehq/editor';
import { makeMeasureText } from '@personalizehq/renderer-web';
import { experienceDocumentSchema } from '@personalizehq/schemas';
import type { AuthoringAssetResolver } from '@personalizehq/schemas';
import type {
  BlockNode,
  ExperienceDocument,
  LayoutSnapshot,
  MeasureText,
  SnapshotNode,
  Viewport,
} from '@personalizehq/schemas/ir';
import { layout } from '@personalizehq/schemas/measure';
import { VIEWPORTS, vpKey } from '@personalizehq/schemas/viewports';

// E7 PREFLIGHT (B4) — the publish gate, run across every step × every conformance viewport, BEFORE
// you publish rather than as a rejection afterwards.
//
// This is a LOOP, NOT A SUBSYSTEM, and that is entirely because we own the solver: `layout()` is a
// pure function of (document, viewport), so asking "what breaks at 2× font scale?" costs one more
// call. No competitor's editor can answer that question, because none of them can run their renderer
// without a device — and 2× font scale is where the customer bug reports come from.
//
// The three laws that only fire at DOCUMENT level (`checkTokenRefs`, `checkProgressTracks`,
// `checkGatedFields` — a dangling token, dots that lie, a `backdrop` with no capability) are
// invisible to the per-node `blockSchema.safeParse` the inspector runs. So preflight parses the WHOLE
// document, which is the only place they are caught.

export type IssueKind = 'schema' | 'overflow' | 'truncated' | 'underfill' | 'collapsed' | 'empty_media';

export interface PreflightIssue {
  kind: IssueKind;
  stepId: string;
  stepName: string;
  /** Absent for a schema issue that belongs to the document rather than one rendering of it. */
  viewport?: Viewport;
  nodeId?: string;
  message: string;
}

/** A surface whose height is the SCREEN's. An `inline` widget is handed a width and answers with a
 *  height — the host scrolls it — so a tall inline document is not a bug. A modal that runs off the
 *  bottom of the phone is. */
const BOUNDED_HEIGHT = new Set(['modal', 'fullscreen', 'banner', 'bottom_sheet']);

/** How far short of the screen a `fullscreen` document may fall before we call it a mistake. Generous
 *  on purpose: a document one line shy of the fold is a rounding artefact, not a bug worth nagging
 *  about. A hugging root is typically hundreds of points short. */
const UNDERFILL_SLACK = 80;

/** `measureText` is injected, defaulting to the real canvas one. The solver is pure but TEXT is not
 *  — it needs a font engine — so this is the single seam between a pure loop and the browser. It is
 *  also what lets the tests pin the geometry with a deterministic ruler rather than depending on
 *  whatever fonts the machine happens to have. */
export function preflight(
  doc: AuthoringDoc,
  measureText: MeasureText = makeMeasureText(),
  resolveAsset?: AuthoringAssetResolver
): PreflightIssue[] {
  const issues: PreflightIssue[] = [];

  // EVERY step list — the default steps and each variant's (ADR 0015). A variant that only the
  // qualify hash will ever surface is exactly the content nobody eyeballs before publish, so
  // skipping it here would ship the one arm that never got checked. Variant steps are labeled
  // "«Variant» · Step" so a row points at the right arm.
  const checkedSteps = [
    ...doc.steps.map((step) => ({ step, stepName: step.name })),
    ...(doc.variants ?? []).flatMap((variant) =>
      variant.steps.map((step) => ({ step, stepName: `${variant.name} · ${step.name}` }))
    ),
  ];

  for (const { step, stepName } of checkedSteps) {
    const at = { stepId: step.id, stepName };
    let compiled: ExperienceDocument;
    try {
      compiled = stepToExperienceDocument(doc, step.id, resolveAsset);
    } catch (error) {
      issues.push({
        ...at,
        kind: 'schema',
        message: error instanceof Error ? error.message : 'Managed media could not be resolved.',
      });
      continue;
    }

    // 1. THE PUBLISH GATE ITSELF. Same schema, same laws, same messages — not a second opinion.
    const parsed = experienceDocumentSchema.safeParse(compiled);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        issues.push({ ...at, kind: 'schema', message: issue.message });
      }
      // A document that does not parse cannot be laid out; its rects would be meaningless.
      continue;
    }

    // 1b. A MEDIA BLOCK WITH NO SOURCE. The schema permits an empty `url` (a just-inserted video
    // defaults to one, and a deleted asset can leave one), so it is not a parse error — but it paints
    // as broken media on the device. Static, so it is checked once per step, not per viewport. C4.
    for (const node of allNodes(parsed.data.root)) {
      if ((node.block === 'image' || node.block === 'video') && node.source.url.trim() === '') {
        issues.push({
          ...at,
          kind: 'empty_media',
          nodeId: node.id,
          message: `${node.block === 'image' ? 'Image' : 'Video'} "${node.id}" has no source — it renders as broken media. Pick an asset or set a URL.`,
        });
      }
    }

    // 2. THE PART ONLY WE CAN DO.
    const caps = maxLines(compiled);
    const fills = fillSpecs(compiled);
    for (const viewport of VIEWPORTS) {
      const snapshot = layout(compiled, viewport, measureText);
      issues.push(...geometryIssues(snapshot, viewport, compiled.surface, caps, fills, at));
    }
  }

  return issues;
}

/** `max_lines` per text id. The SNAPSHOT does not carry it — it carries the resulting `line_count`,
 *  which is the capped value (SPEC §6.11) — so the cap has to come from the document. A repeat's
 *  expansion suffixes ids (`card__0`), so the lookup strips it, exactly as the artboard's hit-test
 *  does. */
function maxLines(doc: ExperienceDocument): Map<string, number> {
  const caps = new Map<string, number>();
  const walk = (n: BlockNode): void => {
    if (n.block === 'text' && n.max_lines !== undefined) caps.set(n.id, n.max_lines);
    if ('children' in n) n.children.forEach(walk);
    if (n.fallback) walk(n.fallback);
  };
  walk(doc.root);
  return caps;
}

/** Which nodes asked for a `fill` on which axis. Read from the DOCUMENT, because the snapshot only
 *  carries the RESULT — and the result of a collapse is a zero, which is indistinguishable from an
 *  author who genuinely wanted a zero-height spacer. */
function fillSpecs(doc: ExperienceDocument): Map<string, { axis: 'width' | 'height' }> {
  const specs = new Map<string, { axis: 'width' | 'height' }>();
  const walk = (n: BlockNode): void => {
    if (n.size?.height === 'fill') specs.set(n.id, { axis: 'height' });
    // A width `fill` collapsing is rarer (it needs a hug-width row), but it vanishes just as silently.
    else if (n.size?.width === 'fill') specs.set(n.id, { axis: 'width' });
    if ('children' in n) n.children.forEach(walk);
    if (n.fallback) walk(n.fallback);
  };
  walk(doc.root);
  return specs;
}

/** Every node in the tree — children, `fallback`, and a `repeat.template` — for the static per-node
 *  checks that do not depend on a viewport. */
function allNodes(root: BlockNode): BlockNode[] {
  const out: BlockNode[] = [];
  const walk = (n: BlockNode): void => {
    out.push(n);
    if ('children' in n) n.children.forEach(walk);
    if (n.fallback) walk(n.fallback);
    if (n.block === 'repeat') walk(n.template);
  };
  walk(root);
  return out;
}

const docId = (id: string): string => id.replace(/__\d+$/, '');

function geometryIssues(
  snapshot: LayoutSnapshot,
  viewport: Viewport,
  surface: string,
  caps: Map<string, number>,
  fills: Map<string, { axis: 'width' | 'height' }>,
  at: { stepId: string; stepName: string }
): PreflightIssue[] {
  const found: PreflightIssue[] = [];
  const root = snapshot.nodes[0];
  if (!root) return found;


  // THE HEADLINE. `hug` does NOT shrink (SPEC §6 — "overflow loudly rather than clamp silently"), so
  // a card sized for one line of text at 1× runs off the screen at 2×. This is exactly the question
  // "show me where 2× font scale breaks", and it is a comparison of two numbers.
  if (BOUNDED_HEIGHT.has(surface) && root.frame[3] > viewport.h) {
    found.push({
      ...at,
      kind: 'overflow',
      viewport,
      nodeId: root.id,
      message: `The ${surface} is taller than the ${viewport.h}pt screen — ${Math.round(root.frame[3] - viewport.h)}pt of it is off the bottom.`,
    });
  }

  // THE MIRROR OF THE CHECK ABOVE, and the one that would have caught the onboarding template on day
  // one. A `fullscreen` step whose document is materially SHORTER than the screen is almost never
  // what the author meant: it leaves the app showing through below the fold, and — the tell — every
  // `fill` child inside it collapses to 0, because a hugging root has no remainder to divide (§6.2).
  // That is exactly how a bottom-pinned button ends up floating in the middle of the screen.
  //
  // Only `fullscreen`. A modal, a sheet and a banner are SUPPOSED to be shorter than the screen —
  // that is what makes them a card rather than a page.
  if (surface === 'fullscreen' && viewport.h - root.frame[3] > UNDERFILL_SLACK) {
    found.push({
      ...at,
      kind: 'underfill',
      viewport,
      nodeId: root.id,
      message: `The fullscreen step only covers ${Math.round(root.frame[3])}pt of the ${viewport.h}pt screen. Give the root \`height: fill\` — otherwise it hugs its content, and any \`fill\` child inside it (a spacer pushing a button to the bottom) collapses to nothing.`,
    });
  }

  for (const node of snapshot.nodes) {
    // A BLOCK THAT VANISHED. `fill` takes a share of the parent's REMAINDER, and a `hug` parent has
    // none (§6.2) — so a `fill` child of one solves to 0pt and simply is not there. The author
    // configures an image, watches it disappear off the canvas, and is told nothing.
    //
    // It is the same root cause as `underfill` seen from the child's side, and it is worth saying
    // separately: "your fullscreen step is short" does not tell you that the image you just set is
    // the thing that went.
    const spec = fills.get(docId(node.id));
    if (spec !== undefined && node.frame[spec.axis === 'height' ? 3 : 2] === 0) {
      found.push({
        ...at,
        kind: 'collapsed',
        viewport,
        nodeId: node.id,
        message: `"${node.id}" has \`${spec.axis}: fill\` but its parent hugs, so there is no space to divide — it renders at 0pt and disappears. Give the parent a height (or \`fill\`), or give this block an explicit size.`,
      });
    }

    // A text the solver CAPPED. `max_lines` caps a count, never the string (SPEC §6.11), so the
    // words are still there and simply do not render — silently, on the device, in a language the
    // author does not read.
    const cap = caps.get(docId(node.id));
    if (cap !== undefined && node.line_count !== undefined && node.line_count >= cap) {
      found.push({
        ...at,
        kind: 'truncated',
        viewport,
        nodeId: node.id,
        message: `Text "${node.id}" fills all ${cap} of its lines — anything longer is cut.`,
      });
    }
  }

  return found;
}

/** Group for display: one row per (kind, node), listing the viewports it breaks at, because "this
 *  card overflows" said five times is noise and "this card overflows at 1.5× and 2×" is a bug report. */
export interface PreflightRow {
  key: string;
  /** WHICH STEP. It was collected all along and never shown — so a three-step document reported "the
   *  fullscreen step only covers 589pt" and left the author to go and find which one. */
  stepId: string;
  stepName: string;
  nodeId?: string | undefined;
  message: string;
  viewports: string[];
}

export function summarize(issues: PreflightIssue[]): PreflightRow[] {
  const rows = new Map<string, PreflightRow>();

  // Keyed by WHAT broke, never by the message: the message quotes a measurement, and the same break
  // measures differently at every scale. Keying on the text would split one bug into five.
  for (const issue of issues) {
    const key = `${issue.stepId}:${issue.kind}:${issue.nodeId ?? ''}`;
    const row =
      rows.get(key) ??
      ({
        key,
        stepId: issue.stepId,
        stepName: issue.stepName,
        nodeId: issue.nodeId,
        message: issue.message,
        viewports: [],
      } satisfies PreflightRow);
    if (issue.viewport) row.viewports.push(vpKey(issue.viewport));
    rows.set(key, row);
  }

  return [...rows.values()];
}

export type { SnapshotNode };
